import React, { useState, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import {
    CircularProgress, Divider, Grid,
    Snackbar, Alert, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    RadioGroup, FormControlLabel, Radio, TextField, FormLabel,
} from '@mui/material';
import { ArrowBack, Edit as EditIcon, Payments as PaymentsIcon, TrendingDown as MoveToSaleIcon, Undo as UndoIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { invoiceService } from '../../../shared/services/invoiceService';
import { clientService } from '../../../shared/services/clientService';
import { Invoice, GST_SLAB_OPTIONS } from '../../../shared/types/invoice';
import { InvoicePdfTemplate } from './InvoicePdfTemplate';

const fmtDate = (d?: string) => {
    if (!d) return '—';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = dt.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
};

export const ViewInvoice: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const backTo = (location.state as { backTo?: string } | null)?.backTo || '/invoices';

    const [invoice, setInvoice] = useState<Invoice | null>(null);
    const [clientGstNumber, setClientGstNumber] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isPdfViewing, setIsPdfViewing] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: 'error' as 'error' | 'success', message: '' });
    const exportTemplateRef = useRef<HTMLDivElement>(null);

    const [moveToSaleOpen, setMoveToSaleOpen] = useState(false);
    const [moveToSaleType, setMoveToSaleType] = useState<'loss' | 'tds'>('loss');
    const [moveToSaleAmount, setMoveToSaleAmount] = useState<string>('');
    const [moveToSaleDate, setMoveToSaleDate] = useState<string>('');
    const [moveToSaleRemark, setMoveToSaleRemark] = useState<string>('');
    const [moveToSaleLoading, setMoveToSaleLoading] = useState(false);
    const [moveToSaleError, setMoveToSaleError] = useState<string>('');
    const [revertLoading, setRevertLoading] = useState(false);

    useEffect(() => {
        if (id) loadInvoice(id);
    }, [id]);

    const toArray = (v: any): string[] => {
        if (Array.isArray(v)) return v;
        if (typeof v === 'string' && v.length > 0) return v.split(',').map(s => s.trim());
        return [];
    };

    const loadInvoice = async (invoiceId: string) => {
        setIsLoading(true);
        try {
            const res = await invoiceService.getById(invoiceId);
            const inv = res.data?.invoice || res.data?.result || (res as any)?.data;
            if (inv) {
                inv.yearForReminder = toArray(inv.yearForReminder);
                inv.optionForReminder = toArray(inv.optionForReminder);
            }
            setInvoice(inv || null);
            // Fetch client to get GST number
            if (inv?.clientId) {
                const clientId = typeof inv.clientId === 'object' ? (inv.clientId._id || (inv.clientId as any).id) : inv.clientId;
                if (clientId) {
                    try {
                        const clientRes = await clientService.getById(clientId);
                        const client = clientRes?.data?.client || clientRes?.data?.result || (clientRes?.data as any);
                        setClientGstNumber(client?.gst || '');
                    } catch {
                        setClientGstNumber('');
                    }
                }
            }
        } catch (err: any) {
            setAlert({ show: true, type: 'error', message: err.response?.data?.message || 'Error loading invoice' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewPdf = async () => {
        if (!invoice || !exportTemplateRef.current) return;
        setIsPdfViewing(true);
        try {
            const element = exportTemplateRef.current;

            // Temporarily remove height constraints so full content renders
            const prevHeight = element.style.minHeight;
            element.style.minHeight = 'auto';

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                height: element.scrollHeight,
                windowHeight: element.scrollHeight,
            });

            element.style.minHeight = prevHeight;

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgHeight = (canvas.height * pdfWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
            heightLeft -= pdfHeight;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
                heightLeft -= pdfHeight;
            }

            window.open(pdf.output('bloburl'), '_blank');
        } catch {
            setAlert({ show: true, type: 'error', message: 'Failed to view PDF' });
        } finally {
            setIsPdfViewing(false);
        }
    };

    const handlePartialPayment = () =>
        navigate(`/invoices/partial-payment/${id}`, { state: { backTo } });

    const handleOpenMoveToSale = () => {
        const pending = invoice
            ? (invoice.totalPendingAmount ?? (invoice.totalAmount - invoice.advancePaidAmount))
            : 0;
        setMoveToSaleAmount(String(pending));
        setMoveToSaleType('loss');
        setMoveToSaleDate(new Date().toISOString().split('T')[0]);
        setMoveToSaleRemark('');
        setMoveToSaleError('');
        setMoveToSaleOpen(true);
    };

    const handleMoveToSaleConfirm = async () => {
        if (!id) return;
        setMoveToSaleError('');
        const amount = parseFloat(moveToSaleAmount);
        if (isNaN(amount) || amount <= 0) {
            setMoveToSaleError('Please enter a valid amount');
            return;
        }
        if (amount > totalPending) {
            setMoveToSaleError('Amount cannot be greater than pending amount');
            return;
        }
        if (!moveToSaleDate) {
            setMoveToSaleError('Please select a date');
            return;
        }
        setMoveToSaleLoading(true);
        try {
            await invoiceService.moveToSale({
                invoiceId: id,
                type: moveToSaleType,
                amount,
                date: moveToSaleDate,
                remark: moveToSaleRemark.trim() || undefined,
            });
            setAlert({ show: true, type: 'success', message: 'Invoice moved to sale successfully' });
            setMoveToSaleOpen(false);
            loadInvoice(id);
        } catch (err: any) {
            setMoveToSaleError(err.response?.data?.message || 'Failed to move invoice to sale');
        } finally {
            setMoveToSaleLoading(false);
        }
    };

    const handleRevertMoveToSale = async () => {
        if (!id) return;
        setRevertLoading(true);
        try {
            if (invoice?.moveToSaleType) {
                await invoiceService.revertMoveToSale(id);
                setAlert({ show: true, type: 'success', message: 'Invoice reverted to outstanding successfully' });
            } else {
                const res = await invoiceService.getPartialPayments(id);
                const payments: any[] = res?.data?.partialPayments || res?.data?.payments || (res as any)?.partialPayments || (res as any)?.payments || res?.data?.data || res?.data?.items || (res as any)?.items || [];
                if (!payments.length) {
                    setAlert({ show: true, type: 'error', message: 'No payments to revert' });
                    return;
                }
                const lastPayment = payments[payments.length - 1];
                const paymentId = lastPayment._id || lastPayment.id;
                await invoiceService.deletePartialPayment(id, paymentId);
                setAlert({ show: true, type: 'success', message: 'Last payment reverted successfully' });
            }
            loadInvoice(id);
        } catch (err: any) {
            setAlert({ show: true, type: 'error', message: err.response?.data?.message || 'Failed to revert invoice' });
        } finally {
            setRevertLoading(false);
        }
    };

    const gstLabel = (slab?: string) => {
        if (!slab) return 'No GST';
        return GST_SLAB_OPTIONS.find(o => o.value === slab)?.label || slab;
    };

    const servicesTotal = invoice?.services?.reduce((s, svc) => s + (Number(svc.amount) || 0), 0) ?? 0;
    const clientName = typeof invoice?.clientId === 'object'
        ? ((invoice?.clientId as any)?.displayName || (invoice?.clientId as any)?.companyName || (invoice?.clientId as any)?.ownerName)
        : invoice?.clientCompanyName;

    const clientAddr = (invoice?.clientId as any);
    const clientAddress = (typeof invoice?.clientId === 'object')
        ? [
            clientAddr?.address,
            [clientAddr?.city, clientAddr?.state].filter(Boolean).join(', '),
            clientAddr?.country
        ].filter(v => v && v !== 'undefined' && v.trim() !== '').join('\n')
        : '';
    const totalPending = invoice
        ? (invoice.totalPendingAmount ?? (invoice.totalAmount - (invoice.advancePaidAmount || 0) - (invoice.partialPaidAmount || 0)))
        : 0;

    const alreadyMovedToSale = !!invoice?.moveToSaleType || totalPending <= 0;
    const canMoveToSale = !alreadyMovedToSale && totalPending > 0;

    const cgstAmount = invoice?.services?.reduce((sum, svc) => sum + (Number(svc.cgstAmount) || 0), 0) ?? 0;
    const sgstAmount = invoice?.services?.reduce((sum, svc) => sum + (Number(svc.sgstAmount) || 0), 0) ?? 0;
    const igstAmount = invoice?.services?.reduce((sum, svc) => sum + (Number(svc.igstAmount) || 0), 0) ?? 0;
    const invoiceTaxSlab = invoice?.services?.[0]?.taxSlab || invoice?.gstSlab;
    const actionBtnSx = { px: 2, minHeight: 34, fontSize: '0.8rem', borderRadius: '8px', whiteSpace: 'nowrap' };
    const invoiceType = invoice?.invoiceType || invoice?.type || 'INV';

    const cell = (label: string, value: React.ReactNode) => (
        <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</span>
            <span className="text-base font-medium text-gray-800 break-words">{value || '—'}</span>
        </div>
    );

    return (
        <div className="add-edit-admin-container p-[40px]">
            <div className="add-edit-admin-content">
                <div className="form-wrapper max-w-5xl">

                    <div className="-ml-4 mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <Link to={backTo} className="back-link">
                            <ArrowBack />
                            <span className="ml-2">Back to Invoices</span>
                        </Link>
                        <div className="flex flex-col gap-2 w-full sm:w-auto sm:items-end">
                            {!!invoice?.moveToSaleType && (
                                <Chip
                                    label={`Moved to Sale (${invoice.moveToSaleType.toUpperCase()})`}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#fffbeb',
                                        color: '#a16207',
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        border: '1px solid #fde68a',
                                    }}
                                />
                            )}
                            <div className="flex flex-wrap gap-2 sm:justify-end">
                                <AppButton
                                    variant="secondary"
                                    startIcon={isPdfViewing ? undefined : <PdfIcon />}
                                    onClick={handleViewPdf}
                                    loading={isPdfViewing}
                                    disabled={isPdfViewing}
                                    sx={{ ...actionBtnSx, color: '#dc2626', borderColor: '#dc2626' }}
                                >
                                    {isPdfViewing ? 'Opening...' : 'View PDF'}
                                </AppButton>
                                <AppButton
                                    variant="secondary"
                                    startIcon={<PaymentsIcon />}
                                    onClick={handlePartialPayment}
                                    sx={actionBtnSx}
                                >
                                    Credited Payment
                                </AppButton>
                                {canMoveToSale && (
                                    <AppButton
                                        variant="danger"
                                        startIcon={<MoveToSaleIcon />}
                                        onClick={handleOpenMoveToSale}
                                        sx={actionBtnSx}
                                    >
                                        Move to Sale
                                    </AppButton>
                                )}
                                {alreadyMovedToSale && (
                                    <AppButton
                                        variant="secondary"
                                        startIcon={revertLoading ? undefined : <UndoIcon />}
                                        onClick={handleRevertMoveToSale}
                                        loading={revertLoading}
                                        sx={actionBtnSx}
                                    >
                                        Revert to Outstanding
                                    </AppButton>
                                )}
                                <AppButton
                                    variant="primary"
                                    startIcon={<EditIcon />}
                                    onClick={() =>
                                        navigate(`/invoices/edit/${id}`, {
                                            state: { from: 'view', backTo },
                                        })
                                    }
                                    sx={actionBtnSx}
                                >
                                    Edit
                                </AppButton>
                            </div>
                        </div>
                    </div>

                    <div className="title text-2xl sm:text-5xl mb-8">View Invoice</div>

                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <CircularProgress size={50} sx={{ color: '#6366f1' }} />
                            <p className="mt-4 text-gray-600 font-medium">Loading invoice...</p>
                        </div>
                    ) : invoice ? (
                        <div className="form-card space-y-6 print:shadow-none">

                            {/* Invoice Header */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-[#0EA5E9] mb-1">Invoice Number</p>
                                    <p className="text-3xl font-extrabold text-gray-900">#{invoice.invoiceNumber}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Invoice Date</p>
                                    <p className="text-lg font-semibold text-gray-700">{fmtDate(invoice.invoiceDate)}</p>
                                </div>
                            </div>

                            <Divider />

                            {/* Client Info */}
                            <div>
                                <p className="text-xs font-bold uppercase tracking-widest text-[#0EA5E9] mb-3">Client Information</p>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        {cell('Client Company', clientName)}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        {cell('Client Address', clientAddress)}
                                    </Grid>
                                </Grid>
                            </div>

                            <Divider />

                            {/* Services Table */}
                            <div>
                                <p className="text-xs font-bold uppercase tracking-widest text-[#0EA5E9] mb-3">Invoice Services</p>
                                <div className="overflow-x-auto rounded-xl border border-gray-200">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-gray-50">
                                                <th className="text-left p-3 font-semibold text-gray-600 text-xs uppercase">#</th>
                                                <th className="text-left p-3 font-semibold text-gray-600 text-xs uppercase">Description</th>
                                                <th className="text-right p-3 font-semibold text-gray-600 text-xs uppercase">Rate/Unit</th>
                                                <th className="text-right p-3 font-semibold text-gray-600 text-xs uppercase">Unit/Hour</th>
                                                <th className="text-right p-3 font-semibold text-gray-600 text-xs uppercase">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(invoice.services || []).map((svc, idx) => (
                                                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                    <td className="p-3 text-gray-500">{idx + 1}</td>
                                                    <td className="p-3 text-gray-800">{svc.description || '—'}</td>
                                                    <td className="p-3 text-right text-gray-700">{svc.ratePerUnitHour || '—'}</td>
                                                    <td className="p-3 text-right text-gray-700">{svc.unitHour || '—'}</td>
                                                    <td className="p-3 text-right font-semibold text-gray-900">{svc.amount || 0}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="border-t border-gray-200">
                                                <td colSpan={4} className="p-3 text-right font-semibold text-gray-600">Services Total</td>
                                                <td className="p-3 text-right font-bold text-gray-900">{servicesTotal}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>

                            <Divider />

                            {/* Tax & Totals */}
                            <div>
                                <p className="text-xs font-bold uppercase tracking-widest text-[#0EA5E9] mb-3">Tax & Summary</p>
                                <div className="max-w-md ml-auto space-y-2">
                                    <div className="flex items-center justify-between text-sm text-gray-600">
                                        <span>Services Total</span>
                                        <span className="font-medium">{servicesTotal}</span>
                                    </div>

                                    {invoiceTaxSlab && (
                                        <div className="flex items-center justify-between gap-2 text-sm text-gray-600">
                                            <span className="flex-shrink-0">GST Slab</span>
                                            <Chip label={gstLabel(invoiceTaxSlab)} size="small" sx={{ backgroundColor: 'rgba(14, 165, 233, 0.12)', color: '#0369a1', fontWeight: 600 }} />
                                        </div>
                                    )}

                                    {invoiceTaxSlab === 'CGST_SGST_9' && (
                                        <>
                                            <div className="flex justify-between text-sm text-gray-600">
                                                <span>CGST (9%)</span>
                                                <span className="font-medium">{cgstAmount}</span>
                                            </div>
                                            <div className="flex justify-between text-sm text-gray-600">
                                                <span>SGST (9%)</span>
                                                <span className="font-medium">{sgstAmount}</span>
                                            </div>
                                        </>
                                    )}

                                    {invoiceTaxSlab === 'IGST_18' && (
                                        <div className="flex justify-between text-sm text-gray-600">
                                            <span>IGST (18%)</span>
                                            <span className="font-medium">{igstAmount}</span>
                                        </div>
                                    )}

                                    {invoice.totalTax > 0 && (
                                        <div className="flex justify-between text-sm text-gray-600">
                                            <span>Total Tax</span>
                                            <span className="font-medium">+ {invoice.totalTax}</span>
                                        </div>
                                    )}

                                    {invoice.totalDiscount > 0 && (
                                        <div className="flex justify-between text-sm text-gray-600">
                                            <span>Total Discount</span>
                                            <span className="font-medium text-green-600">- {invoice.totalDiscount}</span>
                                        </div>
                                    )}

                                    <Divider />

                                    <div className="flex justify-between text-base font-bold text-gray-900">
                                        <span>Total Amount</span>
                                        <span>{invoice.totalAmount}</span>
                                    </div>

                                    {invoice.advancePaidAmount > 0 && (
                                        <div className="flex justify-between text-sm text-gray-600">
                                            <span>Advance Paid</span>
                                            <span className="font-medium text-green-600">- {invoice.advancePaidAmount}</span>
                                        </div>
                                    )}

                                    {invoice.partialPaidAmount !== undefined && invoice.partialPaidAmount > 0 && (
                                        <div className="flex justify-between text-sm text-gray-600">
                                            <span>Credited</span>
                                            <span className="font-medium text-green-600">- {invoice.partialPaidAmount}</span>
                                        </div>
                                    )}

                                    <div className="flex justify-between text-base font-bold text-orange-600 bg-orange-50 rounded-lg px-3 py-2">
                                        <span>Total Pending Amount</span>
                                        <span>{totalPending}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Reminder Settings */}
                            {((invoice.yearForReminder?.length ?? 0) > 0 || (invoice.optionForReminder?.length ?? 0) > 0) && (
                                <>
                                    <Divider />
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-[#0EA5E9] mb-3">Reminder Settings</p>
                                        <div className="flex flex-wrap gap-6">
                                            {(invoice.yearForReminder?.length ?? 0) > 0 && (
                                                <div className="flex-1" style={{ minWidth: '180px' }}>
                                                    <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Year For Reminder</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {invoice.yearForReminder?.map(y => (
                                                            <Chip key={y} label={y} size="small" sx={{ backgroundColor: '#f3f4f6', color: '#374151' }} />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {(invoice.optionForReminder?.length ?? 0) > 0 && (
                                                <div className="flex-1" style={{ minWidth: '180px' }}>
                                                    <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Option For Reminder</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {invoice.optionForReminder?.map(o => (
                                                            <Chip key={o} label={o} size="small" sx={{ backgroundColor: '#f3f4f6', color: '#374151' }} />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Additional Note */}
                            {invoice.additionalNote && (
                                <>
                                    <Divider />
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-[#0EA5E9] mb-2">Additional Note</p>
                                        <p className="text-gray-700 text-sm leading-relaxed">{invoice.additionalNote}</p>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="form-card flex items-center justify-center py-20 text-gray-400">
                            Invoice not found.
                        </div>
                    )}
                </div>
            </div>

            {invoice && (
                <InvoicePdfTemplate
                    ref={exportTemplateRef}
                    invoice={invoice}
                    clientName={clientName}
                    clientAddress={clientAddress}
                    clientGst={clientGstNumber}
                    invoiceType={invoiceType}
                    servicesTotal={servicesTotal}
                    invoiceTaxSlab={invoiceTaxSlab}
                    cgstAmount={cgstAmount}
                    sgstAmount={sgstAmount}
                    igstAmount={igstAmount}
                />
            )}

            <Snackbar
                open={alert.show}
                autoHideDuration={4000}
                onClose={() => setAlert(a => ({ ...a, show: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={alert.type} variant="filled" className="rounded-xl">
                    {alert.message}
                </Alert>
            </Snackbar>

            {/* Move to Sale Dialog */}
            <Dialog
                open={moveToSaleOpen}
                onClose={() => !moveToSaleLoading && setMoveToSaleOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', pb: 0 }}>
                    Move Invoice to Sale
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    {moveToSaleError && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                            {moveToSaleError}
                        </Alert>
                    )}
                    <p className="text-sm text-gray-500 mb-4">
                        Pending amount of <span className="font-semibold text-gray-800">₹{totalPending}</span> will be written off.
                        Choose the write-off type and enter the amount.
                    </p>

                    <FormLabel sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Write-off Type
                    </FormLabel>
                    <RadioGroup
                        row
                        value={moveToSaleType}
                        onChange={(e) => {
                            setMoveToSaleType(e.target.value as 'loss' | 'tds');
                            setMoveToSaleError('');
                        }}
                        sx={{ mt: 0.5, mb: 3 }}
                    >
                        <FormControlLabel
                            value="loss"
                            control={<Radio size="small" />}
                            label={<span className="text-sm font-medium">Loss</span>}
                        />
                        <FormControlLabel
                            value="tds"
                            control={<Radio size="small" />}
                            label={<span className="text-sm font-medium">TDS</span>}
                        />
                    </RadioGroup>

                    <TextField
                        label="Date"
                        type="date"
                        fullWidth
                        value={moveToSaleDate}
                        onChange={(e) => {
                            setMoveToSaleDate(e.target.value);
                            setMoveToSaleError('');
                        }}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' }, mb: 2 }}
                    />

                    <TextField
                        label="Write-off Amount"
                        type="number"
                        fullWidth
                        value={moveToSaleAmount}
                        onChange={(e) => {
                            setMoveToSaleAmount(e.target.value);
                            setMoveToSaleError('');
                        }}
                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        onKeyDown={(e) => {
                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                            }
                        }}
                        error={!!moveToSaleError && (moveToSaleError.includes('Amount') || moveToSaleError.includes('amount'))}
                        inputProps={{ min: 0, max: totalPending, step: 'any' }}
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                            mb: 2,
                            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                'WebkitAppearance': 'none',
                                margin: 0,
                            },
                            '& input[type=number]': {
                                'MozAppearance': 'textfield',
                            },
                        }}
                    />

                    <TextField
                        label="Remark"
                        fullWidth
                        value={moveToSaleRemark}
                        onChange={(e) => {
                            setMoveToSaleRemark(e.target.value);
                            setMoveToSaleError('');
                        }}
                        size="small"
                        placeholder="Enter remark (optional)"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <AppButton
                        variant="cancel"
                        onClick={() => setMoveToSaleOpen(false)}
                        disabled={moveToSaleLoading}
                        sx={{ px: 3 }}
                    >
                        Cancel
                    </AppButton>
                    <AppButton
                        variant="danger"
                        onClick={handleMoveToSaleConfirm}
                        loading={moveToSaleLoading}
                        sx={{ px: 3 }}
                    >
                        Confirm
                    </AppButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

